/**
 * Plates: the nightly dream's chapter images as keepsakes. Each plate carries a code fingerprinted from its story, its
 * place in that story and the image's own bytes, and a sigil drawn from the code, so a saved plate can always be traced
 * back to the one story and moment that made it. Shared by the worker (minting) and the client (sigil, saved file).
 */

/** One plate in the player's album (doc `loom:plates`). Derived output only: the picture, its caption, the story title. */
export interface Plate {
  story: string;
  /** Index of the image in its story (plate number i + 1). */
  i: number;
  code: string;
  caption: string;
  title: string;
  drawnAt: number;
}
export interface PlateIndex { plates: Plate[] }
export const PLATES_DOC = "loom:plates";

const hex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
const sha256 = async (data: ArrayBuffer | string) =>
  hex(await crypto.subtle.digest("SHA-256", typeof data === "string" ? new TextEncoder().encode(data).buffer as ArrayBuffer : data));

/** "7F2A-91C3": SHA-256 over the story id, the plate's place in it and the hash of the image bytes. */
export async function plateCode(story: string, i: number, image: ArrayBuffer): Promise<string> {
  const h = (await sha256(`loom-plate:v1:${story}:${i}:${await sha256(image)}`)).slice(0, 8).toUpperCase();
  return `${h.slice(0, 4)}-${h.slice(4)}`;
}

export const CODE_RE = /^[0-9A-F]{4}-[0-9A-F]{4}$/;

/** I, II, III … for plate numbers. */
export function roman(n: number): string {
  const r: [number, string][] = [[1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let out = "";
  for (const [v, s] of r) while (n >= v) { out += s; n -= v; }
  return out || "0";
}

export const plateLabel = (i: number, code: string) => `Plate ${roman(i + 1)} · ${code}`;

/**
 * The sigil: seven points on a circle, joined by the chords the code's bits choose (at least three), and one
 * filled point. Coordinates are in a 0–100 box. The same code always draws the same sigil.
 */
export function sigil(code: string): { points: [number, number][]; chords: [number, number][]; mark: number } {
  const bits = parseInt(code.replace("-", ""), 16) >>> 0;
  const N = 7;
  const turn = ((bits >>> 27) & 7) / 8; // a small rotation, so sigils don't all stand on the same point
  const points = Array.from({ length: N }, (_, k): [number, number] => {
    const a = ((k / N) + turn / N) * Math.PI * 2 - Math.PI / 2;
    return [50 + 40 * Math.cos(a), 50 + 40 * Math.sin(a)];
  });
  const all: [number, number][] = [];
  for (let a = 0; a < N; a++) for (let b = a + 1; b < N; b++) all.push([a, b]);
  let chords = all.filter((_, k) => (bits >>> k) & 1); // 21 chords, 21 bits
  if (chords.length < 3) chords = [...chords, ...all.filter((c) => !chords.includes(c)).slice(0, 3 - chords.length)];
  if (chords.length > 9) chords = chords.filter((_, k) => k % 2 === 0).slice(0, 9); // keep it a mark, not a scribble
  return { points, chords, mark: (bits >>> 21) % N };
}
